import { useState, useEffect, useCallback, useRef } from "react";
import {
  Server, RefreshCw, Plus, ShieldCheck, ShieldOff, ShieldAlert,
  AlertTriangle, CheckCircle2, XCircle, Clock, Activity, Zap,
  Database, Wifi, WifiOff, ChevronRight, Loader2, Edit2, Trash2,
  KeyRound, RotateCcw, Download, Terminal, Info
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { tokenStore } from "@/lib/api";
import { toast } from "sonner";

const BASE = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${tokenStore.getAccess()}`, "Content-Type": "application/json" });

// safeFetch: wraps fetch() to handle "body stream already read" from proxy/CDN
const safeFetch = async (url, options) => {
  const res = await fetch(url, options);
  let body = {};
  try {
    const text = await res.text();
    if (text) body = JSON.parse(text);
  } catch (_) {}
  return { ok: res.ok, status: res.status, json: () => body };
};

// ── Style helpers ─────────────────────────────────────────────────────────────
const statusBadge = (s) => ({
  online:        "bg-emerald-50 text-emerald-700 border-emerald-200",
  offline:       "bg-slate-100 text-slate-500 border-slate-200",
  expiring_soon: "bg-amber-50  text-amber-700  border-amber-200",
  expired:       "bg-red-50    text-red-700    border-red-200",
  revoked:       "bg-red-100   text-red-800    border-red-300",
  decommissioned:"bg-slate-100 text-slate-400  border-slate-200",
  unhealthy:     "bg-orange-50 text-orange-700 border-orange-200",
})[s] || "bg-slate-100 text-slate-500 border-slate-200";

const statusDot = (s) => ({
  online: "bg-emerald-400 animate-pulse",
  offline: "bg-slate-300",
  expiring_soon: "bg-amber-400",
  expired: "bg-red-400",
  revoked: "bg-red-600",
  decommissioned: "bg-slate-300",
})[s] || "bg-slate-300";

const certColor = (c) => ({
  green:  "border-l-emerald-400 bg-emerald-50/50",
  yellow: "border-l-amber-400   bg-amber-50/50",
  red:    "border-l-red-400     bg-red-50/50",
})[c] || "";

const certBadge = (c) => ({
  green:  "bg-emerald-50 text-emerald-700 border-emerald-200",
  yellow: "bg-amber-50   text-amber-700   border-amber-200",
  red:    "bg-red-50     text-red-700     border-red-200",
})[c] || "";

const poolColor = (l) => ({
  high:     "bg-emerald-500",
  normal:   "bg-cyan-500",
  low:      "bg-amber-500",
  critical: "bg-orange-500",
  depleted: "bg-red-500",
})[l] || "bg-slate-300";

const poolBadge = (l) => ({
  high:     "bg-emerald-50 text-emerald-700 border-emerald-200",
  normal:   "bg-cyan-50    text-cyan-700    border-cyan-200",
  low:      "bg-amber-50   text-amber-700   border-amber-200",
  critical: "bg-orange-50  text-orange-700  border-orange-200",
  depleted: "bg-red-50     text-red-700     border-red-200",
})[l] || "";

const fmtBytes = (b) => {
  if (!b) return "0 B";
  if (b >= 1073741824) return `${(b / 1073741824).toFixed(2)} GB`;
  if (b >= 1048576)    return `${(b / 1048576).toFixed(2)} MB`;
  if (b >= 1024)       return `${(b / 1024).toFixed(1)} KB`;
  return `${b} B`;
};

const fmtDate = (s) => s ? new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

// ── Main Component ────────────────────────────────────────────────────────────
export default function DeviceManagement() {
  const [tab, setTab]           = useState("overview");
  const [overview, setOverview] = useState(null);
  const [devices,  setDevices]  = useState([]);
  const [certs,    setCerts]    = useState(null);
  const [pool,     setPool]     = useState(null);
  const [loading,  setLoading]  = useState({});
  const [lastUpd,  setLastUpd]  = useState(null);

  // Dialogs
  const [registerOpen, setRegisterOpen] = useState(false);
  const [editDevice,   setEditDevice]   = useState(null);
  const [detailDevice, setDetailDevice] = useState(null);
  const [revokeDevice, setRevokeDevice] = useState(null);
  const [revokeReason, setRevokeReason] = useState("");
  const [actionBusy,   setActionBusy]   = useState(false);

  const set = (key, val) => setLoading(p => ({ ...p, [key]: val }));

  // ── Fetchers ────────────────────────────────────────────────────────────────
  const fetchOverview = useCallback(async () => {
    set("overview", true);
    try {
      const r = await fetch(`${BASE}/api/v1/devices/registry/overview`, { headers: auth() });
      if (r.ok) { setOverview(await r.json()); setLastUpd(new Date()); }
    } finally { set("overview", false); }
  }, []);

  const fetchDevices = useCallback(async () => {
    set("devices", true);
    try {
      const r = await fetch(`${BASE}/api/v1/devices/registry/`, { headers: auth() });
      if (r.ok) { const d = await r.json(); setDevices(d.devices || []); }
    } finally { set("devices", false); }
  }, []);

  const fetchCerts = useCallback(async () => {
    set("certs", true);
    try {
      const r = await fetch(`${BASE}/api/v1/devices/certs/dashboard`, { headers: auth() });
      if (r.ok) setCerts(await r.json());
    } finally { set("certs", false); }
  }, []);

  const fetchPool = useCallback(async () => {
    set("pool", true);
    try {
      const r = await fetch(`${BASE}/api/v1/entropy/pool-status`, { headers: auth() });
      if (r.ok) setPool(await r.json());
    } finally { set("pool", false); }
  }, []);

  const seedDevices = async () => {
    const r = await fetch(`${BASE}/api/v1/devices/registry/seed`, { method: "POST", headers: auth() });
    const d = await r.json();
    toast.success(d.message || "Seeded");
    fetchDevices(); fetchOverview(); fetchCerts();
  };

  // Initial load
  useEffect(() => {
    fetchOverview(); fetchDevices(); fetchCerts(); fetchPool();
    const iv = setInterval(() => { fetchOverview(); fetchPool(); }, 30000);
    return () => clearInterval(iv);
  }, [fetchOverview, fetchDevices, fetchCerts, fetchPool]);

  // ── Actions ─────────────────────────────────────────────────────────────────
  const handleRevoke = async () => {
    if (!revokeReason.trim() || revokeReason.trim().length < 5) {
      toast.error("Please enter a reason (min 5 chars)"); return;
    }
    setActionBusy(true);
    try {
      const r = await safeFetch(`${BASE}/api/v1/devices/registry/${revokeDevice.device_id}/revoke`, {
        method: "POST", headers: auth(),
        body: JSON.stringify({ reason: revokeReason }),
      });
      if (r.ok) {
        toast.success(`Device ${revokeDevice.device_id} revoked`);
        setRevokeDevice(null); setRevokeReason("");
        fetchDevices(); fetchOverview(); fetchCerts();
      } else {
        const e = r.json(); toast.error(e.detail || "Revoke failed");
      }
    } finally { setActionBusy(false); }
  };

  const handleRenew = async (dev) => {
    setActionBusy(true);
    try {
      const r = await safeFetch(`${BASE}/api/v1/devices/registry/${dev.device_id}/renew`, { method: "POST", headers: auth() });
      const d = r.json();
      if (r.ok) toast.success(`Renewal initiated for ${dev.device_id}`);
      else toast.error(d.detail || "Renewal failed");
      fetchDevices();
    } finally { setActionBusy(false); }
  };

  const handleDecommission = async (dev) => {
    if (!window.confirm(`Decommission ${dev.device_id}? This cannot be undone.`)) return;
    const r = await safeFetch(`${BASE}/api/v1/devices/registry/${dev.device_id}`, { method: "DELETE", headers: auth() });
    if (r.ok) { toast.success(`${dev.device_id} decommissioned`); fetchDevices(); fetchOverview(); }
    else { const e = r.json(); toast.error(e.detail || "Failed"); }
  };

  const handleDeployCRL = async () => {
    const r = await safeFetch(`${BASE}/api/v1/devices/certs/crl/deploy`, { method: "POST", headers: auth() });
    const d = r.json();
    if (r.ok) { toast.success(`CRL deployed — ${d.entries_deployed} entries`); fetchCerts(); }
    else toast.error("Deploy failed");
  };

  const handleSimulate = async () => {
    const r = await safeFetch(`${BASE}/api/v1/entropy/simulate`, {
      method: "POST", headers: auth(),
      body: JSON.stringify({ device_id: "sim-device-01", bytes: 32768 }),
    });
    const d = r.json();
    if (r.ok) { toast.success(`Simulated: ${fmtBytes(d.bytes_injected)} injected — pool ${d.pool_fill_pct}%`); fetchPool(); fetchOverview(); }
    else toast.error(d.detail || "Simulation failed");
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div data-testid="device-management-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" data-testid="device-mgmt-title">
            QRNG Device Registry
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage QRNG devices, mTLS certificate lifecycle, and entropy pool — architecture per QRaaS PKI spec
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastUpd && (
            <span className="text-[11px] text-slate-400 flex items-center gap-1.5">
              <Activity className="w-3 h-3" /> {lastUpd.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={() => { fetchOverview(); fetchDevices(); fetchCerts(); fetchPool(); }}
            className="inline-flex items-center gap-2 border border-slate-200 text-slate-600 hover:bg-slate-50 text-xs px-4 py-2 transition-colors"
            data-testid="refresh-all-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading.overview ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100 h-9 mb-6">
          {[
            { val: "overview", label: "Fleet Overview" },
            { val: "registry", label: "Device Registry" },
            { val: "certs",    label: "Certificate Dashboard" },
            { val: "pool",     label: "Entropy Pool" },
          ].map(t => (
            <TabsTrigger key={t.val} value={t.val} className="text-xs h-8 px-4" data-testid={`tab-${t.val}`}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* ── TAB 1: FLEET OVERVIEW ────────────────────────────────────────── */}
        <TabsContent value="overview">
          {overview && (
            <>
              {/* Device summary cards */}
              <div className="grid grid-cols-7 gap-3 mb-6">
                {[
                  { label: "Total",         value: overview.devices.total,          color: "text-slate-900" },
                  { label: "Online",        value: overview.devices.online,         color: "text-emerald-600" },
                  { label: "Offline",       value: overview.devices.offline,        color: "text-slate-500" },
                  { label: "Expiring Soon", value: overview.devices.expiring_soon,  color: "text-amber-600" },
                  { label: "Expired",       value: overview.devices.expired,        color: "text-red-500" },
                  { label: "Revoked",       value: overview.devices.revoked,        color: "text-red-700" },
                  { label: "Active Conns",  value: overview.active_connections,     color: "text-cyan-600" },
                ].map((s, i) => (
                  <div key={i} className="bg-white border border-slate-200 p-3" data-testid={`overview-stat-${i}`}>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider">{s.label}</div>
                    <div className={`text-xl font-bold font-mono mt-0.5 ${s.color}`}>{s.value}</div>
                  </div>
                ))}
              </div>

              {/* Entropy pool banner */}
              <EntropyPoolBanner pool={overview.entropy_pool} onSimulate={handleSimulate} />

              {/* Device status list */}
              <div className="bg-white border border-slate-200 mt-6" data-testid="overview-device-list">
                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Device Status</span>
                  <span className="text-[11px] text-slate-400">Passive monitoring — based on incoming entropy</span>
                </div>
                <DeviceStatusList devices={devices} onSelect={setDetailDevice} />
              </div>
            </>
          )}
          {!overview && loading.overview && <LoadingState label="Loading fleet overview..." />}
        </TabsContent>

        {/* ── TAB 2: DEVICE REGISTRY ───────────────────────────────────────── */}
        <TabsContent value="registry">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-slate-500">
              Zero private keys stored — certificate metadata only (CN, serial, expiry)
            </p>
            <div className="flex items-center gap-2">
              {devices.length === 0 && (
                <button onClick={seedDevices} className="text-xs text-slate-500 hover:text-slate-700 border border-dashed border-slate-300 px-3 py-1.5 hover:bg-slate-50" data-testid="seed-devices-btn">
                  Seed demo data
                </button>
              )}
              <button onClick={() => setRegisterOpen(true)} className="inline-flex items-center gap-2 bg-slate-900 text-white text-xs px-4 py-2 hover:bg-slate-700 transition-colors" data-testid="register-device-btn">
                <Plus className="w-3.5 h-3.5" /> Register Device
              </button>
            </div>
          </div>

          <div className="bg-white border border-slate-200" data-testid="device-registry-table">
            {loading.devices && devices.length === 0 ? (
              <LoadingState label="Loading device registry..." />
            ) : devices.length === 0 ? (
              <EmptyState icon={Server} label="No devices registered" action="Use 'Register Device' to add a QRNG device" />
            ) : (
              <RegistryTable
                devices={devices}
                onDetail={setDetailDevice}
                onRevoke={(d) => { setRevokeDevice(d); setRevokeReason(""); }}
                onRenew={handleRenew}
                onDecommission={handleDecommission}
                onEdit={setEditDevice}
              />
            )}
          </div>
        </TabsContent>

        {/* ── TAB 3: CERTIFICATE DASHBOARD ─────────────────────────────────── */}
        <TabsContent value="certs">
          {certs ? (
            <>
              {/* Summary */}
              <div className="grid grid-cols-5 gap-3 mb-6">
                {[
                  { label: "Total Certs",   value: certs.summary.total,         color: "text-slate-900" },
                  { label: "Valid",         value: certs.summary.valid,         color: "text-emerald-600" },
                  { label: "Expiring <30d", value: certs.summary.expiring_soon, color: "text-amber-600" },
                  { label: "Critical <7d",  value: certs.summary.critical,      color: "text-red-500" },
                  { label: "Expired",       value: certs.summary.expired,       color: "text-red-700" },
                ].map((s, i) => (
                  <div key={i} className="bg-white border border-slate-200 p-3" data-testid={`cert-stat-${i}`}>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider">{s.label}</div>
                    <div className={`text-xl font-bold font-mono mt-0.5 ${s.color}`}>{s.value}</div>
                  </div>
                ))}
              </div>

              {/* CRL banner */}
              <div className="mb-6 bg-white border border-slate-200 p-4 flex items-center justify-between" data-testid="crl-banner">
                <div>
                  <div className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                    <ShieldOff className="w-4 h-4 text-slate-500" /> Certificate Revocation List (CRL)
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {certs.crl.total_entries} total revocations &middot;{" "}
                    {certs.crl.pending_deployment > 0 ? (
                      <span className="text-amber-600 font-medium">{certs.crl.pending_deployment} pending Gateway 01 deployment</span>
                    ) : (
                      <span className="text-emerald-600">All deployed to Gateway 01</span>
                    )}
                  </div>
                </div>
                {certs.crl.pending_deployment > 0 && (
                  <button onClick={handleDeployCRL} className="inline-flex items-center gap-2 bg-slate-900 text-white text-xs px-4 py-2 hover:bg-slate-700" data-testid="deploy-crl-btn">
                    <Download className="w-3.5 h-3.5" /> Deploy CRL to Gateway 01
                  </button>
                )}
              </div>

              {/* Cert cards */}
              <div className="grid grid-cols-1 gap-2" data-testid="cert-cards">
                {certs.certificates.map((c, i) => (
                  <CertCard key={i} cert={c} onRenew={() => { const dev = devices.find(d => d.device_id === c.device_id); if (dev) handleRenew(dev); }} />
                ))}
              </div>

              {/* Architecture note */}
              <div className="mt-6 p-4 border border-slate-200 bg-slate-50 text-xs text-slate-500 flex items-start gap-2">
                <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>
                  <strong className="text-slate-700">Architecture:</strong> Root CA private key is offline/HSM.
                  Device private keys remain on physical devices — never stored here.
                  Renewal requires offline CSR signing against Root CA.
                  Revoked serials must be deployed to NGINX Gateway 01 (ssl_crl) and nginx -s reload executed.
                </span>
              </div>
            </>
          ) : loading.certs ? (
            <LoadingState label="Loading certificate dashboard..." />
          ) : null}
        </TabsContent>

        {/* ── TAB 4: ENTROPY POOL ──────────────────────────────────────────── */}
        <TabsContent value="pool">
          {pool ? (
            <EntropyPoolTab pool={pool} onRefresh={fetchPool} onSimulate={handleSimulate} onRequest={async () => {
              const r = await safeFetch(`${BASE}/api/v1/entropy/request`, { method: "POST", headers: auth(), body: JSON.stringify({ bytes_requested: 65536, priority: "high" }) });
              const d = r.json();
              if (r.ok) toast.success(`Request sent to ${d.sent} device(s)`);
              else toast.error(d.detail || "Request failed");
            }} />
          ) : loading.pool ? (
            <LoadingState label="Loading entropy pool..." />
          ) : null}
        </TabsContent>
      </Tabs>

      {/* ── Dialogs ───────────────────────────────────────────────────────── */}
      <RegisterDeviceDialog
        open={registerOpen}
        onClose={() => setRegisterOpen(false)}
        onSaved={() => { fetchDevices(); fetchOverview(); fetchCerts(); setRegisterOpen(false); }}
      />

      <EditDeviceDialog
        device={editDevice}
        onClose={() => setEditDevice(null)}
        onSaved={() => { fetchDevices(); setEditDevice(null); }}
      />

      <DeviceDetailDialog device={detailDevice} onClose={() => setDetailDevice(null)} />

      {/* Revoke dialog */}
      <Dialog open={!!revokeDevice} onOpenChange={() => setRevokeDevice(null)}>
        <DialogContent className="sm:max-w-md" data-testid="revoke-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2">
              <ShieldOff className="w-4 h-4" /> Revoke Certificate
            </DialogTitle>
            <DialogDescription>
              Revoke <strong>{revokeDevice?.device_id}</strong> ({revokeDevice?.certificate_cn}). This adds the serial to the CRL and closes any active connections.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-xs text-slate-600">Revocation Reason *</Label>
            <Textarea
              className="mt-1.5 text-sm"
              placeholder="e.g. Hardware failure, key compromise, device decommissioned..."
              value={revokeReason}
              onChange={e => setRevokeReason(e.target.value)}
              rows={3}
              data-testid="revoke-reason-input"
            />
          </div>
          <DialogFooter>
            <button onClick={() => setRevokeDevice(null)} className="text-xs text-slate-600 border border-slate-200 px-4 py-2 hover:bg-slate-50">Cancel</button>
            <button onClick={handleRevoke} disabled={actionBusy} className="bg-red-600 text-white text-xs px-4 py-2 hover:bg-red-700 disabled:opacity-50" data-testid="confirm-revoke-btn">
              {actionBusy ? "Revoking..." : "Revoke Certificate"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function LoadingState({ label }) {
  return (
    <div className="flex items-center justify-center py-16 text-slate-400 gap-2">
      <Loader2 className="w-5 h-5 animate-spin" /> <span className="text-sm">{label}</span>
    </div>
  );
}

function EmptyState({ icon: Icon, label, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-slate-400 gap-2">
      <Icon className="w-8 h-8 mb-1 text-slate-300" />
      <span className="text-sm font-medium text-slate-500">{label}</span>
      {action && <span className="text-xs text-slate-400">{action}</span>}
    </div>
  );
}

function EntropyPoolBanner({ pool, onSimulate }) {
  if (!pool) return null;
  const pct = pool.fill_percentage || 0;
  return (
    <div className={`border p-4 ${pct === 0 ? "border-red-200 bg-red-50" : pct < 10 ? "border-orange-200 bg-orange-50" : pct < 30 ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-white"}`} data-testid="entropy-pool-banner">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Database className="w-4 h-4 text-slate-500" />
          <span className="text-sm font-semibold text-slate-800">Entropy Pool</span>
          <Badge variant="outline" className={`text-[10px] uppercase ${poolBadge(pool.level)}`}>{pool.level}</Badge>
          {pool.dev_mode && <Badge variant="outline" className="text-[10px] bg-violet-50 text-violet-600 border-violet-200">DEV MODE</Badge>}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs font-mono text-slate-600">{fmtBytes(pool.current_bytes)} / {fmtBytes(pool.capacity_bytes)}</span>
          {pool.dev_mode && (
            <button onClick={onSimulate} className="text-xs text-violet-600 border border-violet-200 hover:bg-violet-50 px-3 py-1 flex items-center gap-1" data-testid="simulate-btn">
              <Terminal className="w-3 h-3" /> Simulate Push
            </button>
          )}
        </div>
      </div>
      <div className="relative h-3 bg-slate-100 overflow-hidden">
        <div className={`h-full transition-all duration-700 ${poolColor(pool.level)}`} style={{ width: `${pct}%` }} />
        {/* Watermark lines */}
        <div className="absolute top-0 h-full w-px bg-amber-400/70" style={{ left: "30%" }} title="Low watermark 30%" />
        <div className="absolute top-0 h-full w-px bg-emerald-400/70" style={{ left: "80%" }} title="High watermark 80%" />
      </div>
      <div className="flex items-center justify-between mt-1.5">
        <span className="text-[10px] text-slate-400">{pct.toFixed(1)}% full</span>
        <span className="text-[10px] text-slate-400">Low: 30% &nbsp; High: 80%</span>
      </div>
      {pct === 0 && (
        <p className="text-xs text-red-700 mt-2 font-medium">
          Pool depleted — API returns HTTP 503. Connect QRNG devices or use Simulate Push (dev mode).
        </p>
      )}
    </div>
  );
}

function DeviceStatusList({ devices, onSelect }) {
  if (!devices.length) return <EmptyState icon={Server} label="No devices" action="Register devices in the Registry tab" />;
  return (
    <div>
      <div className="grid grid-cols-[1fr_100px_130px_110px_160px_36px] bg-slate-50 border-b border-slate-100">
        {["Device", "Status", "Last Seen", "Throughput", "NIST SP800-90B", ""].map((h, i) => (
          <div key={i} className="px-4 py-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wide">{h}</div>
        ))}
      </div>
      {devices.map((d, i) => (
        <div key={i} className="grid grid-cols-[1fr_100px_130px_110px_160px_36px] border-b border-slate-50 hover:bg-slate-50/60 cursor-pointer transition-colors" onClick={() => onSelect(d)} data-testid={`device-row-${i}`}>
          <div className="px-4 py-3 flex items-center gap-2.5">
            <div className={`w-2 h-2 rounded-full shrink-0 ${statusDot(d.status)}`} />
            <div>
              <div className="text-sm font-mono font-semibold text-slate-900">{d.device_id}</div>
              <div className="text-[10px] text-slate-400">{d.location || "—"}</div>
            </div>
          </div>
          <div className="px-4 py-3 flex items-center">
            <Badge variant="outline" className={`text-[10px] uppercase tracking-wide ${statusBadge(d.status)}`}>{d.status}</Badge>
          </div>
          <div className="px-4 py-3 flex items-center text-[11px] text-slate-600 font-mono">
            {d.last_seen ? new Date(d.last_seen).toLocaleTimeString() : "Never"}
          </div>
          <div className="px-4 py-3 flex items-center text-xs font-mono text-slate-600">
            {d.entropy_throughput_bps ? `${(d.entropy_throughput_bps / 1024).toFixed(0)} KB/s` : "—"}
          </div>
          <div className="px-4 py-3 flex items-center">
            {d.last_nist_result ? (
              <Badge variant="outline" className={`text-[10px] ${d.last_nist_result.overall === "PASS" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                {d.last_nist_result.overall}
              </Badge>
            ) : <span className="text-[11px] text-slate-400">N/A</span>}
          </div>
          <div className="px-3 py-3 flex items-center"><ChevronRight className="w-3.5 h-3.5 text-slate-300" /></div>
        </div>
      ))}
    </div>
  );
}

function RegistryTable({ devices, onDetail, onRevoke, onRenew, onDecommission, onEdit }) {
  return (
    <div>
      <div className="grid grid-cols-[1fr_90px_140px_100px_110px_130px_140px] bg-slate-50 border-b border-slate-200">
        {["Device ID", "Status", "Certificate CN", "Serial", "Cert Expiry", "Registered", "Actions"].map((h, i) => (
          <div key={i} className="px-4 py-2.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wide">{h}</div>
        ))}
      </div>
      {devices.map((d, i) => (
        <div key={i} className="grid grid-cols-[1fr_90px_140px_100px_110px_130px_140px] border-b border-slate-100 hover:bg-slate-50/40 transition-colors" data-testid={`registry-row-${i}`}>
          <div className="px-4 py-3 flex items-center gap-2 cursor-pointer" onClick={() => onDetail(d)}>
            <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${statusDot(d.status)}`} />
            <span className="text-sm font-mono font-semibold text-slate-900">{d.device_id}</span>
          </div>
          <div className="px-4 py-3 flex items-center">
            <Badge variant="outline" className={`text-[10px] uppercase ${statusBadge(d.status)}`}>{d.status}</Badge>
          </div>
          <div className="px-4 py-3 flex items-center text-[11px] text-slate-600 font-mono truncate" title={d.certificate_cn}>
            {d.certificate_cn?.replace(".entropy.qcoresecure.local", "") || "—"}
          </div>
          <div className="px-4 py-3 flex items-center text-[11px] font-mono text-slate-500 truncate" title={d.certificate_serial}>
            {d.certificate_serial?.slice(0, 11) || "—"}…
          </div>
          <div className={`px-4 py-3 flex items-center text-[11px] font-mono ${d.days_to_expiry != null && d.days_to_expiry < 30 ? "text-amber-600 font-semibold" : d.days_to_expiry != null && d.days_to_expiry < 7 ? "text-red-600 font-bold" : "text-slate-600"}`}>
            {d.days_to_expiry != null ? `${d.days_to_expiry}d` : "—"}
          </div>
          <div className="px-4 py-3 flex items-center text-[11px] text-slate-500">
            {fmtDate(d.registered_at)}
          </div>
          <div className="px-4 py-3 flex items-center gap-1.5">
            {!d.revoked_at && !d.is_decommissioned && (
              <>
                <button onClick={() => onEdit(d)} className="text-slate-400 hover:text-slate-700 p-1" title="Edit" data-testid={`edit-btn-${i}`}><Edit2 className="w-3.5 h-3.5" /></button>
                <button onClick={() => onRenew(d)} className="text-amber-500 hover:text-amber-700 p-1" title="Renew cert" data-testid={`renew-btn-${i}`}><RotateCcw className="w-3.5 h-3.5" /></button>
                <button onClick={() => onRevoke(d)} className="text-red-400 hover:text-red-700 p-1" title="Revoke" data-testid={`revoke-btn-${i}`}><ShieldOff className="w-3.5 h-3.5" /></button>
              </>
            )}
            <button onClick={() => onDecommission(d)} className="text-slate-300 hover:text-red-500 p-1" title="Decommission" data-testid={`decomm-btn-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

function CertCard({ cert, onRenew }) {
  return (
    <div className={`border-l-4 p-4 border border-slate-200 flex items-center justify-between ${certColor(cert.color)}`} data-testid="cert-card">
      <div className="flex items-center gap-4">
        <ShieldCheck className={`w-5 h-5 shrink-0 ${cert.color === "green" ? "text-emerald-500" : cert.color === "yellow" ? "text-amber-500" : "text-red-500"}`} />
        <div>
          <div className="text-sm font-mono font-semibold text-slate-900">{cert.device_id}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">{cert.certificate_cn}</div>
          <div className="text-[10px] text-slate-400 mt-0.5">Serial: {cert.certificate_serial}</div>
        </div>
      </div>
      <div className="flex items-center gap-6 text-right">
        <div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wide">Issued</div>
          <div className="text-xs font-mono text-slate-600">{fmtDate(cert.certificate_issued_at)}</div>
        </div>
        <div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wide">Expires</div>
          <div className={`text-xs font-mono font-semibold ${cert.color === "green" ? "text-slate-700" : cert.color === "yellow" ? "text-amber-600" : "text-red-600"}`}>
            {fmtDate(cert.certificate_expires_at)}
          </div>
        </div>
        <div>
          <Badge variant="outline" className={`text-[10px] ${certBadge(cert.color)}`}>
            {cert.days_to_expiry != null ? (cert.days_to_expiry < 0 ? "EXPIRED" : `${cert.days_to_expiry}d left`) : "—"}
          </Badge>
        </div>
        {cert.color !== "green" && (
          <button onClick={onRenew} className="text-xs text-amber-600 border border-amber-200 hover:bg-amber-50 px-3 py-1.5 flex items-center gap-1" data-testid={`cert-renew-${cert.device_id}`}>
            <RotateCcw className="w-3 h-3" /> Renew
          </button>
        )}
      </div>
    </div>
  );
}

function EntropyPoolTab({ pool, onRefresh, onSimulate, onRequest }) {
  const pct = pool.fill_percentage || 0;
  return (
    <div className="space-y-6">
      {/* Pool gauge */}
      <div className="bg-white border border-slate-200 p-6" data-testid="pool-gauge">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <Database className="w-4 h-4 text-slate-500" /> Entropy Pool Status
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Filled by QRNG devices via authenticated WebSocket. Consumed by QRNG API endpoints.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={`text-[10px] uppercase ${poolBadge(pool.level)}`}>{pool.level}</Badge>
            {pool.dev_mode && <Badge variant="outline" className="text-[10px] bg-violet-50 text-violet-600 border-violet-200">DEV MODE</Badge>}
          </div>
        </div>

        {/* Large gauge */}
        <div className="relative h-8 bg-slate-100 overflow-hidden mb-1">
          <div className={`h-full transition-all duration-700 ${poolColor(pool.level)}`} style={{ width: `${pct}%` }} />
          <div className="absolute inset-0 flex items-center justify-center text-xs font-bold text-white mix-blend-difference">
            {pct.toFixed(1)}%
          </div>
          <div className="absolute top-0 h-full w-0.5 bg-amber-400" style={{ left: "10%" }} title="Critical 10%" />
          <div className="absolute top-0 h-full w-0.5 bg-amber-500" style={{ left: "30%" }} title="Low 30%" />
          <div className="absolute top-0 h-full w-0.5 bg-emerald-500" style={{ left: "80%" }} title="High 80%" />
        </div>
        <div className="flex justify-between text-[10px] text-slate-400 mb-4">
          <span>0%</span>
          <span className="text-orange-400">Critical 10%</span>
          <span className="text-amber-500">Low 30%</span>
          <span className="text-emerald-500">High 80%</span>
          <span>100%</span>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-4 gap-4 border-t border-slate-100 pt-4">
          {[
            { label: "Current",   value: fmtBytes(pool.current_bytes),       mono: true },
            { label: "Capacity",  value: fmtBytes(pool.capacity_bytes),       mono: true },
            { label: "Received",  value: fmtBytes(pool.total_received_bytes), mono: true },
            { label: "Served",    value: fmtBytes(pool.total_served_bytes),   mono: true },
          ].map((s, i) => (
            <div key={i}>
              <div className="text-[10px] text-slate-400 uppercase tracking-wide">{s.label}</div>
              <div className={`text-sm font-semibold text-slate-800 mt-0.5 ${s.mono ? "font-mono" : ""}`}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* Active connections */}
        <div className="mt-4 border-t border-slate-100 pt-4">
          <div className="flex items-center gap-2 text-xs text-slate-600 mb-2">
            <Wifi className={`w-3.5 h-3.5 ${pool.active_connections > 0 ? "text-emerald-500" : "text-slate-300"}`} />
            <span><strong>{pool.active_connections}</strong> device(s) connected via mTLS WebSocket</span>
          </div>
          {pool.connected_devices?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {pool.connected_devices.map(id => (
                <Badge key={id} variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">{id}</Badge>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="grid grid-cols-3 gap-3">
        <button onClick={onRefresh} className="border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm py-3 flex items-center justify-center gap-2" data-testid="pool-refresh-btn">
          <RefreshCw className="w-4 h-4" /> Refresh Status
        </button>
        <button onClick={onRequest} className="border border-cyan-200 text-cyan-700 hover:bg-cyan-50 text-sm py-3 flex items-center justify-center gap-2" data-testid="request-entropy-btn">
          <Zap className="w-4 h-4" /> Request Entropy (all devices)
        </button>
        {pool.dev_mode && (
          <button onClick={onSimulate} className="border border-violet-200 text-violet-600 hover:bg-violet-50 text-sm py-3 flex items-center justify-center gap-2" data-testid="pool-simulate-btn">
            <Terminal className="w-4 h-4" /> Simulate Device Push
          </button>
        )}
      </div>

      {/* 503 warning */}
      {pool.level === "depleted" && (
        <div className="border border-red-200 bg-red-50 p-4 text-sm text-red-700 flex items-start gap-2" data-testid="depleted-warning">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>
            <strong>Pool Depleted.</strong> QRNG API endpoints are returning HTTP 503.
            {pool.dev_mode ? " In dev mode, endpoints fall back to OS CSPRNG." : " Connect QRNG devices to restore service."}
            Never serves pseudo-random data in production mode.
          </span>
        </div>
      )}
    </div>
  );
}

// ── Register Device Dialog ──────────────────────────────────────────────────

function RegisterDeviceDialog({ open, onClose, onSaved }) {
  const empty = { device_id: "", certificate_cn: "", certificate_serial: "", certificate_issued_at: "", certificate_expires_at: "", location: "", hardware_model: "", firmware_version: "", notes: "" };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  useEffect(() => { if (!open) setForm(empty); }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.device_id || !form.certificate_cn || !form.certificate_serial || !form.certificate_expires_at) {
      toast.error("Fill all required fields"); return;
    }
    setBusy(true);
    try {
      const body = { ...form };
      if (!body.certificate_issued_at) delete body.certificate_issued_at;
      const r = await safeFetch(`${BASE}/api/v1/devices/registry/`, { method: "POST", headers: auth(), body: JSON.stringify(body) });
      const d = r.json();
      if (r.ok) { toast.success(`Device ${form.device_id} registered`); onSaved(); }
      else toast.error(d.detail || "Registration failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="register-device-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Server className="w-4 h-4" /> Register QRNG Device</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Certificate metadata only — no private keys. Device private key stays on hardware.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <Section title="Device Identity">
            <Field label="Device ID *" placeholder="qrng-01" value={form.device_id} onChange={f("device_id")} testId="field-device-id" />
            <Field label="Location" placeholder="DC-Mumbai-1, Rack A3" value={form.location} onChange={f("location")} />
            <Field label="Hardware Model" placeholder="QE-2000 Silicon Photonics QRNG" value={form.hardware_model} onChange={f("hardware_model")} />
            <Field label="Firmware Version" placeholder="v2.4.1" value={form.firmware_version} onChange={f("firmware_version")} />
          </Section>
          <Section title="Certificate Metadata (no private keys)">
            <Field label="Certificate CN *" placeholder="qrng-01.entropy.qcoresecure.local" value={form.certificate_cn} onChange={f("certificate_cn")} testId="field-cert-cn" />
            <Field label="Certificate Serial *" placeholder="0A:1B:2C:3D:4E:5F:60:71" value={form.certificate_serial} onChange={f("certificate_serial")} testId="field-cert-serial" />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Issued At" type="date" value={form.certificate_issued_at?.slice(0,10)} onChange={e => setForm(p => ({ ...p, certificate_issued_at: e.target.value ? e.target.value + "T00:00:00Z" : "" }))} />
              <Field label="Expires At *" type="date" value={form.certificate_expires_at?.slice(0,10)} onChange={e => setForm(p => ({ ...p, certificate_expires_at: e.target.value + "T00:00:00Z" }))} testId="field-cert-expiry" />
            </div>
          </Section>
          <Section title="Notes">
            <div>
              <Label className="text-xs text-slate-600">Notes (optional)</Label>
              <Textarea className="mt-1 text-sm" rows={2} value={form.notes} onChange={f("notes")} placeholder="Free-form admin notes" />
            </div>
          </Section>
          <DialogFooter>
            <button type="button" onClick={onClose} className="text-xs text-slate-600 border border-slate-200 px-4 py-2 hover:bg-slate-50">Cancel</button>
            <button type="submit" disabled={busy} className="bg-slate-900 text-white text-xs px-4 py-2 hover:bg-slate-700 disabled:opacity-50" data-testid="save-device-btn">
              {busy ? "Registering..." : "Register Device"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditDeviceDialog({ device, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (device) setForm({ location: device.location || "", hardware_model: device.hardware_model || "", firmware_version: device.firmware_version || "", notes: device.notes || "", certificate_serial: device.certificate_serial || "", certificate_expires_at: device.certificate_expires_at?.slice(0,10) || "" });
  }, [device]);
  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));
  const handleSave = async () => {
    setBusy(true);
    const body = { ...form };
    if (body.certificate_expires_at) body.certificate_expires_at = body.certificate_expires_at + "T00:00:00Z";
    const r = await safeFetch(`${BASE}/api/v1/devices/registry/${device.device_id}`, { method: "PUT", headers: auth(), body: JSON.stringify(body) });
    const d = r.json();
    if (r.ok) { toast.success("Device updated"); onSaved(); }
    else toast.error(d.detail || "Update failed");
    setBusy(false);
  };
  return (
    <Dialog open={!!device} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="edit-device-dialog">
        <DialogHeader><DialogTitle>Edit {device?.device_id}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <Field label="Location" value={form.location} onChange={f("location")} />
          <Field label="Hardware Model" value={form.hardware_model} onChange={f("hardware_model")} />
          <Field label="Firmware Version" value={form.firmware_version} onChange={f("firmware_version")} />
          <Field label="Certificate Serial" value={form.certificate_serial} onChange={f("certificate_serial")} />
          <Field label="Cert Expires At" type="date" value={form.certificate_expires_at} onChange={f("certificate_expires_at")} />
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea className="mt-1 text-sm" rows={2} value={form.notes} onChange={f("notes")} />
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="text-xs border border-slate-200 px-4 py-2 hover:bg-slate-50">Cancel</button>
          <button onClick={handleSave} disabled={busy} className="bg-slate-900 text-white text-xs px-4 py-2 hover:bg-slate-700 disabled:opacity-50" data-testid="save-edit-btn">
            {busy ? "Saving..." : "Save Changes"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeviceDetailDialog({ device: d, onClose }) {
  if (!d) return null;
  return (
    <Dialog open={!!d} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-xl max-h-[80vh] overflow-y-auto" data-testid="device-detail-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className={`w-2.5 h-2.5 rounded-full ${statusDot(d.status)}`} />
            <DialogTitle className="font-mono text-lg">{d.device_id}</DialogTitle>
            <Badge variant="outline" className={`text-[10px] uppercase ${statusBadge(d.status)}`}>{d.status}</Badge>
          </div>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <DetailSection title="Device Info">
            {[
              ["Certificate CN", d.certificate_cn],
              ["Certificate Serial", d.certificate_serial],
              ["Location", d.location || "—"],
              ["Hardware Model", d.hardware_model || "—"],
              ["Firmware", d.firmware_version || d.last_health_report?.firmware || "—"],
              ["Last Seen", d.last_seen ? new Date(d.last_seen).toLocaleString() : "Never"],
              ["Registered By", d.registered_by],
              ["Registered At", fmtDate(d.registered_at)],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between py-1.5 border-b border-slate-50 last:border-0">
                <span className="text-xs text-slate-500">{k}</span>
                <span className="text-xs font-mono text-slate-800 max-w-[60%] text-right break-all">{v || "—"}</span>
              </div>
            ))}
          </DetailSection>
          {d.last_health_report && (
            <DetailSection title="Last Health Report">
              {Object.entries(d.last_health_report).map(([k, v]) => (
                <div key={k} className="flex justify-between py-1.5 border-b border-slate-50 last:border-0">
                  <span className="text-xs text-slate-500 capitalize">{k.replace(/_/g, " ")}</span>
                  <span className="text-xs font-mono text-slate-800">{String(v)}</span>
                </div>
              ))}
            </DetailSection>
          )}
          {d.last_nist_result && (
            <DetailSection title="NIST SP800-90B Tests">
              {Object.entries(d.last_nist_result).map(([k, v]) => (
                <div key={k} className="flex justify-between items-center py-1.5 border-b border-slate-50 last:border-0">
                  <span className="text-xs text-slate-500 capitalize">{k.replace(/_/g, " ")}</span>
                  <span className={`text-xs font-mono font-semibold ${v === "PASS" ? "text-emerald-600" : v === "WARN" ? "text-amber-600" : "text-slate-500"}`}>{v}</span>
                </div>
              ))}
            </DetailSection>
          )}
          {d.revoked_at && (
            <div className="p-3 bg-red-50 border border-red-100 text-xs text-red-700">
              <strong>Revoked</strong> on {fmtDate(d.revoked_at)} by {d.revoked_by || "—"}<br />
              Reason: {d.revocation_reason}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Tiny helpers ──────────────────────────────────────────────────────────────
function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">{title}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function DetailSection({ title, children }) {
  return (
    <div className="border border-slate-100 p-3">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">{title}</div>
      {children}
    </div>
  );
}

function Field({ label, placeholder, value, onChange, type = "text", testId }) {
  return (
    <div>
      <Label className="text-xs text-slate-600">{label}</Label>
      <Input type={type} className="mt-1 h-8 text-sm" placeholder={placeholder} value={value || ""} onChange={onChange} data-testid={testId} />
    </div>
  );
}
